// Kas & Bank — Breakdown Tab (klasifikasi baris e-statement + auto-pair transfer).
// 2026-09-23 (user request).
// 2026-09-23 (user request): sub-tab "Belum Diklasifikasi" digabung ke tab ini.
// 2026-09-23 (user request): kolom tabel mengikuti "Contoh Output Fitur
// Breakdown.xlsx" — label kolom bisa di-rename (ikon pensil di judul kolom),
// tombol Export Excel utk baris yang sudah di-breakdown/dikelompokkan.
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { Tag, Shuffle, X, Save, AlertCircle, Layers, CheckSquare, Square, FileDown, Pencil, ChevronDown, ChevronRight, Split, Ban, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import {
  listAllRows, breakdownRow, bulkBreakdownRows, fetchTransferCandidates, pairTransfer,
  exportBreakdownClassified, getExportColumns, saveExportColumns,
  splitEstatementRow, unsplitEstatementRow, setNoBreakdown,
} from "@/lib/kasBankEstatement";
import { KIND_META, KIND_ORDER, fetchCOA, suggestCOA, buildCoaGroupsWithSuggestions, buildProjectOptions } from "@/lib/kasBankClassification";
import ClassificationListTab from "./ClassificationListTab";
import SearchableSelect from "@/components/SearchableSelect";

const DEFAULT_COLS = [
  { key: "date", label: "DATE" },
  { key: "invoice_no", label: "INVOICE / PI NO" },
  { key: "po_no", label: "NO Purchase Order" },
  { key: "doc_no", label: "KODE VOUCHER" },
  { key: "description", label: "DESCRIPTION" },
  { key: "credit", label: "CREDIT (IN)" },
  { key: "debit", label: "DEBIT (OUT)" },
  { key: "balance", label: "SALDO" },
  { key: "pic_name", label: "PIC / Vendor Name" },
  { key: "keterangan_pembelian", label: "Keterangan Pembelian" },
  { key: "project_name", label: "Proyek" },
  { key: "coa", label: "AKUN COA" },
];

const NUM_COLS = new Set(["credit", "debit", "balance"]);

export default function BreakdownTab({ canWrite, canClsWrite, canClsDelete }) {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [onlyUnbreakdown, setOnlyUnbreakdown] = useState(true);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState({ pairs: [], pair_count: 0 });
  const [editing, setEditing] = useState(null);
  const [coa, setCoa] = useState([]);
  const [selected, setSelected] = useState(new Set()); // set of row id
  const [bulkOpen, setBulkOpen] = useState(false);
  const [colConfig, setColConfig] = useState(DEFAULT_COLS);
  const [renaming, setRenaming] = useState(null); // key kolom yang sedang di-rename
  const [exporting, setExporting] = useState(false);
  const [projects, setProjects] = useState([]);
  // 2026-09-24 (user request): pisah 2 tabel panjang jadi sub-tab agar tidak
  // perlu scroll jauh — "Breakdown E-Statement" | "Belum Diklasifikasi".
  const [bdSub, setBdSub] = useState("estatement");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, r, c] = await Promise.all([
        api.get("/company-bank-accounts").then((x) => x.data || []),
        listAllRows({ account_id: accountId || undefined, only_unbreakdown: onlyUnbreakdown }),
        fetchTransferCandidates(),
      ]);
      setAccounts(a);
      setRows(r);
      setCandidates(c);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat.");
    } finally { setLoading(false); }
  }, [accountId, onlyUnbreakdown]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchCOA().then(setCoa).catch(() => setCoa([])); }, []);
  useEffect(() => { getExportColumns().then((c) => { if (c.length) setColConfig(c); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/active-projects").then((r) => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([])); }, []);

  const accMap = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);

  // Auto-bersihkan seleksi yang sudah tidak ada di data (mis. setelah reload).
  useEffect(() => {
    setSelected((prev) => {
      const valid = new Set(rows.map((r) => r.id));
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);

  // 2026-02-13 (user request): expand trace split + tanda "Tidak Breakdown".
  const [expandedSplits, setExpandedSplits] = useState(new Set());
  const [nbBusy, setNbBusy] = useState(null);
  const toggleSplitExpand = (id) => setExpandedSplits((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleNoBreakdown = async (r, excluded) => {
    const msg = excluded
      ? `Tandai transaksi ini sebagai "Tidak Breakdown"? Baris keluar dari daftar belum di-breakdown.`
      : `Batalkan tanda "Tidak Breakdown"? Baris kembali ke daftar belum di-breakdown.`;
    if (!window.confirm(msg)) return;
    try {
      setNbBusy(r.id);
      await setNoBreakdown(r.id, excluded);
      toast.success(excluded ? "Ditandai Tidak Breakdown." : "Tanda Tidak Breakdown dibatalkan.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengubah tanda.");
    } finally { setNbBusy(null); }
  };

  const applyPair = async (pair) => {
    try {
      await pairTransfer({ row_id_a: pair.out.id, row_id_b: pair.in.id });
      toast.success("Pasangan transfer dikonfirmasi.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal konfirmasi."); }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportBreakdownClassified({ account_id: accountId || undefined });
      toast.success("Ekspor Excel diunduh.");
    } catch (err) {
      let msg = "Gagal export Excel.";
      try {
        const t = await err?.response?.data?.text?.();
        if (t) msg = JSON.parse(t).detail || msg;
      } catch { /* abaikan */ }
      toast.error(msg);
    } finally { setExporting(false); }
  };

  const doRename = async (key, label) => {
    const next = colConfig.map((c) => (c.key === key ? { ...c, label } : c));
    try {
      const saved = await saveExportColumns(next);
      setColConfig(saved.length ? saved : next);
      toast.success("Nama kolom disimpan.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan nama kolom.");
    }
    setRenaming(null);
  };

  const cellVal = (r, key) => {
    switch (key) {
      case "date":
        return <span className="tabular-nums whitespace-nowrap">{fmtDate(r.date)}</span>;
      case "description":
        return <span className="text-slate-800">{r.description || "—"}</span>;
      case "credit":
        return r.direction === "in"
          ? <span className="tabular-nums font-bold text-emerald-700">{formatIDR(r.amount)}</span>
          : <span className="text-slate-300">—</span>;
      case "debit":
        return r.direction === "out"
          ? <span className="tabular-nums font-bold text-rose-700">{formatIDR(r.amount)}</span>
          : <span className="text-slate-300">—</span>;
      case "balance":
        return r.balance != null
          ? <span className="tabular-nums">{formatIDR(r.balance)}</span>
          : <span className="text-slate-300">—</span>;
      case "coa":
        return r.coa_code ? `${r.coa_code} · ${r.coa_name || ""}` : "—";
      case "doc_no":
        return r.doc_no || "—";
      default:
        return r[key] || "—";
    }
  };

  return (
    <div data-testid="bd-tab">
      <div className="flex items-center gap-1 mb-2" data-testid="bd-subtabs">
        {[
          { key: "estatement", label: "Breakdown E-Statement" },
          { key: "unclassified", label: "Belum Diklasifikasi" },
        ].map((s) => (
          <button key={s.key} type="button" onClick={() => setBdSub(s.key)} data-testid={`bd-sub-${s.key}`}
            className={`inline-flex items-center gap-1 px-2.5 py-1 border text-[10px] uppercase tracking-wider font-semibold transition-colors ${bdSub === s.key ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-500 border-slate-300 hover:border-[#0B1D3A] hover:text-[#0B1D3A]"}`}>
            {s.label}
          </button>
        ))}
      </div>
    {bdSub === "estatement" && (
    <div className="bg-white border border-slate-200" data-testid="bd-estatement-panel">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <Tag size={15} className="text-[#0B1D3A]" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Breakdown E-Statement</div>
        <span className="text-[10px] text-slate-400 italic">Klasifikasi baris + COA · auto-pair transfer 2 sisi</span>
        <div className="ml-auto flex items-center gap-2">
          {canWrite && (
            <button type="button" onClick={() => setBulkOpen(true)} disabled={selected.size === 0} data-testid="bd-bulk-open"
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FF5A1F] hover:bg-[#e34d16] disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold transition-colors">
              <Layers size={11} /> Klasifikasi Massal ({selected.size})
            </button>
          )}
          <button type="button" onClick={handleExport} disabled={exporting} data-testid="bd-export"
            title="Ekspor Excel baris yang SUDAH di-breakdown (1 sheet per rekening × periode)"
            className="inline-flex items-center gap-1 px-2.5 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 text-[10px] uppercase tracking-wider font-semibold transition-colors">
            <FileDown size={11} /> {exporting ? "Menyiapkan…" : "Ekspor Excel"}
          </button>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="bd-account"
            className="border border-slate-300 px-2 py-1 text-[11px] bg-white">
            <option value="">Semua Rekening</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.bank_name} · {a.account_no}</option>)}
          </select>
          <label className="flex items-center gap-1 text-[10px] text-slate-600">
            <input type="checkbox" checked={onlyUnbreakdown} onChange={(e) => setOnlyUnbreakdown(e.target.checked)} data-testid="bd-only-unbreakdown" />
            Hanya belum di-breakdown
          </label>
        </div>
      </div>

      {candidates.pair_count > 0 && (
        <div className="px-4 py-2 bg-sky-50 border-b border-sky-200" data-testid="bd-candidates">
          <div className="text-[11px] font-semibold text-sky-900 mb-1 flex items-center gap-1"><Shuffle size={12} /> Kandidat Transfer Otomatis ({candidates.pair_count})</div>
          <div className="space-y-1">
            {candidates.pairs.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px] bg-white border border-sky-200 px-2 py-1" data-testid={`bd-pair-${i}`}>
                <span className="tabular-nums text-slate-500">{fmtDate(p.out.date)}</span>
                <span className="text-rose-700">Keluar {accMap[p.out.account_id]?.bank_name || "?"} · {p.out.description?.slice(0, 40)}</span>
                <span className="text-slate-400">↔</span>
                <span className="tabular-nums text-slate-500">{fmtDate(p.in.date)}</span>
                <span className="text-emerald-700">Masuk {accMap[p.in.account_id]?.bank_name || "?"} · {p.in.description?.slice(0, 40)}</span>
                <span className="ml-auto tabular-nums font-bold text-[#0B1D3A]">{formatIDR(p.out.amount)}</span>
                {canWrite && (
                  <button type="button" onClick={() => applyPair(p)} data-testid={`bd-confirm-pair-${i}`}
                    className="px-2 py-0.5 bg-sky-700 hover:bg-sky-800 text-white text-[10px] uppercase font-semibold">Confirm Transfer</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-center text-[11px] italic text-slate-400">Memuat…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-[11px] text-slate-400" data-testid="bd-empty">Tidak ada baris pada filter ini.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]" data-testid="bd-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[9px] uppercase tracking-wider text-slate-600">
              <tr>
                {/* 2026-10-01 (user request): header sticky — konsisten dgn tabel
                    Report Petty Cash & Register Buku Bank. */}
                <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-left px-2 py-2 w-8">
                  {canWrite && rows.length > 0 && (
                    <button type="button" onClick={toggleAll} data-testid="bd-select-all"
                      title={allSelected ? "Batalkan semua" : "Pilih semua"}
                      className="inline-flex items-center justify-center text-slate-600 hover:text-[#0B1D3A]">
                      {allSelected ? <CheckSquare size={14} className="text-[#FF5A1F]" /> : <Square size={14} />}
                    </button>
                  )}
                </th>
                {colConfig.map((c) => (
                  <th key={c.key} className={`sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] px-3 py-2 ${NUM_COLS.has(c.key) ? "text-right" : "text-left"}`}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {canWrite && (
                        <button type="button" onClick={() => setRenaming(c.key)} title="Ubah nama kolom"
                          data-testid={`bd-rename-${c.key}`}
                          className="text-slate-300 hover:text-[#FF5A1F] transition-colors">
                          <Pencil size={9} />
                        </button>
                      )}
                    </span>
                  </th>
                ))}
                <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-left px-3 py-2">Rekening</th>
                <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-left px-3 py-2">Auto-Match</th>
                <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-left px-3 py-2">Jenis</th>
                <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-right px-3 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const km = r.breakdown_kind ? KIND_META[r.breakdown_kind] : null;
                const isSel = selected.has(r.id);
                // 2026-02-13 (user request): penanda split + tanda "Tidak Breakdown".
                const splitParts = r.split_parts || [];
                const isSplit = splitParts.length > 0;
                const isOpen = expandedSplits.has(r.id);
                const fullColSpan = colConfig.length + 5;
                return (
                  <Fragment key={r.id}>
                  <tr className={`border-b border-slate-100 ${isSel ? "bg-amber-50/60" : "hover:bg-slate-50"}`} data-testid={`bd-row-${r.id}`}>
                    <td className="px-2 py-1.5">
                      {canWrite && (
                        <button type="button" onClick={() => toggleRow(r.id)} data-testid={`bd-select-${r.id}`}
                          className="inline-flex items-center justify-center text-slate-500 hover:text-[#FF5A1F]">
                          {isSel ? <CheckSquare size={13} className="text-[#FF5A1F]" /> : <Square size={13} />}
                        </button>
                      )}
                    </td>
                    {colConfig.map((c) => (
                      <td key={c.key} className={`px-3 py-1.5 ${NUM_COLS.has(c.key) ? "text-right" : ""}`} data-testid={`bd-cell-${c.key}-${r.id}`}>{cellVal(r, c.key)}</td>
                    ))}
                    <td className="px-3 py-1.5 text-[10px]">{accMap[r.account_id]?.bank_name} · {accMap[r.account_id]?.account_no}</td>
                    <td className="px-3 py-1.5">
                      {r.matched_movement_id ? (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200" title={r.matched_description}>Tercatat</span>
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-slate-100 text-slate-600 border border-slate-200">Belum</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {isSplit ? (
                        <button type="button" onClick={() => toggleSplitExpand(r.id)} data-testid={`bd-split-toggle-${r.id}`}
                          title="Lihat pecahan & riwayat split (sebelum → sesudah)"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-violet-50 text-violet-700 border border-violet-300 hover:bg-violet-100">
                          {isOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />} Split {splitParts.length} bagian
                        </button>
                      ) : r.no_breakdown ? (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-slate-100 text-slate-500 border border-slate-300"
                          title={`Ditandai oleh ${r.no_breakdown_by_name || "-"}${r.no_breakdown_at ? ` · ${fmtDate(r.no_breakdown_at)}` : ""}`}
                          data-testid={`bd-nobreakdown-chip-${r.id}`}>Tidak Breakdown</span>
                      ) : km ? (
                        <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold border ${km.classes}`}>{km.label}</span>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">Belum</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {canWrite && (
                        <div className="inline-flex items-center gap-1 justify-end">
                          <button type="button" onClick={() => setEditing(r)} data-testid={`bd-edit-${r.id}`}
                            className="px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold">Breakdown</button>
                          {!isSplit && !km && (
                            r.no_breakdown ? (
                              <button type="button" onClick={() => toggleNoBreakdown(r, false)} disabled={nbBusy === r.id} data-testid={`bd-nobreakdown-undo-${r.id}`}
                                title="Batalkan tanda 'Tidak Breakdown'"
                                className="px-2 py-1 border border-slate-300 text-slate-500 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">Batal Tanda</button>
                            ) : (
                              <button type="button" onClick={() => toggleNoBreakdown(r, true)} disabled={nbBusy === r.id} data-testid={`bd-nobreakdown-${r.id}`}
                                title="Transaksi ini tidak perlu di-breakdown"
                                className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 text-slate-500 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
                                <Ban size={10} /> Tidak Breakdown
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {isSplit && isOpen && (
                    <tr className="bg-violet-50/40 border-b border-violet-100" data-testid={`bd-split-detail-${r.id}`}>
                      <td colSpan={fullColSpan} className="px-4 py-3">
                        <SplitTrace row={r} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <BreakdownDialog row={editing} coa={coa} projects={projects} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {bulkOpen && selectedRows.length > 0 && (
        <BulkBreakdownDialog rows={selectedRows} coa={coa} onClose={() => setBulkOpen(false)} onSaved={() => { setBulkOpen(false); setSelected(new Set()); load(); }} />
      )}
      {renaming && (
        <RenameColumnDialog colKey={renaming} current={colConfig.find((c) => c.key === renaming)?.label || ""}
          onClose={() => setRenaming(null)} onSave={doRename} />
      )}
    </div>
    )}

    {bdSub === "unclassified" && (
    <div data-testid="bd-unclassified-section">
      <div className="px-4 py-2 bg-amber-50 border border-amber-200 flex items-center gap-2">
        <AlertCircle size={13} className="text-amber-700" />
        <span className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Belum Diklasifikasi — Mutasi Sistem</span>
        <span className="text-[10px] text-amber-700/70 italic">Mutasi bank internal yang belum diberi klasifikasi (penerimaan / pembayaran / transfer)</span>
      </div>
      <ClassificationListTab kind="unclassified" canWrite={canClsWrite} canDelete={canClsDelete} />
    </div>
    )}
    </div>
  );
}

function RenameColumnDialog({ colKey, current, onClose, onSave }) {
  const [label, setLabel] = useState(current);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { toast.error("Nama kolom tidak boleh kosong."); return; }
    setSaving(true);
    await onSave(colKey, label.trim());
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="bd-rename-dialog">
      <form onSubmit={submit} className="bg-white w-full max-w-sm border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="overline text-[10px] text-slate-500 tracking-widest">UBAH NAMA KOLOM</div>
            <div className="font-display text-sm font-semibold text-[#0B1D3A]">Berlaku di tabel ini & export Excel</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-4">
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} autoFocus data-testid="bd-rename-input"
            className="w-full border border-slate-300 px-2 py-1.5 text-[12px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50/70">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">Batal</button>
          <button type="submit" disabled={saving} data-testid="bd-rename-save"
            className="px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
            {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * 2026-02-13 (user request): Trace split — tampilan sebelum → sesudah + riwayat.
 * Dipakai di expand row tabel Breakdown & di panel kelola split pada dialog.
 */
function SplitTrace({ row }) {
  const parts = row.split_parts || [];
  const history = row.split_history || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[10px]">
      <div className="border border-violet-200 bg-white p-3" data-testid={`bd-split-trace-${row.id}`}>
        <div className="overline text-[9px] text-slate-500 tracking-widest mb-1.5">Sebelum → Sesudah Split</div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-1 bg-slate-100 border border-slate-300 font-semibold tabular-nums text-slate-700" data-testid={`bd-split-before-${row.id}`}>
            1 transaksi · {formatIDR(row.amount)}
          </span>
          <span className="text-slate-400">→</span>
          <span className="px-2 py-1 bg-violet-100 border border-violet-300 font-semibold text-violet-800">
            {parts.length} pecahan
          </span>
        </div>
        <div className="mt-2 space-y-1">
          {parts.map((p) => (
            <div key={p.id} className="flex items-center gap-2 border-b border-slate-100 pb-1" data-testid={`bd-split-part-${row.id}-${p.id}`}>
              <span className="w-8 text-slate-400 tabular-nums">#{p.seq}</span>
              <span className="tabular-nums font-bold text-[#0B1D3A]">{formatIDR(p.amount)}</span>
              {p.note && <span className="text-slate-500 truncate max-w-[160px]" title={p.note}>· {p.note}</span>}
              <span className="ml-auto">
                {p.klasifikasi ? (
                  <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {p.klasifikasi.coa_code} · {p.klasifikasi.coa_name}
                  </span>
                ) : (
                  <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-amber-50 text-amber-700 border border-amber-200">Belum diklasifikasi</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="border border-slate-200 bg-white p-3">
        <div className="overline text-[9px] text-slate-500 tracking-widest mb-1.5">Riwayat Perubahan</div>
        {history.length === 0 ? (
          <div className="text-slate-400 italic">—</div>
        ) : (
          <div className="space-y-1.5">
            {[...history].reverse().map((h, i) => (
              <div key={i} className="flex items-start gap-2" data-testid={`bd-split-history-${row.id}-${i}`}>
                <span className={`mt-0.5 inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold border ${h.action === "split" ? "bg-violet-50 text-violet-700 border-violet-300" : "bg-rose-50 text-rose-700 border-rose-300"}`}>
                  {h.action === "split" ? "Split" : "Batal Split"}
                </span>
                <div className="flex-1">
                  <div className="text-slate-700 tabular-nums">
                    {(h.parts || []).map((p) => formatIDR(p.amount)).join(" + ")}
                    {h.action === "unsplit" && (h.wiped_classifications || 0) > 0 && (
                      <span className="text-rose-600"> · {h.wiped_classifications} klasifikasi pecahan dihapus</span>
                    )}
                  </div>
                  <div className="text-[9px] text-slate-400">{fmtDate(h.at)} · oleh {h.by_name || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Editor pecahan nominal (2..10 baris; total wajib = nominal transaksi). */
function SplitEditor({ row, parts, setParts }) {
  const total = parts.reduce((a, p) => a + (parseFloat(String(p.amount).replace(/[^\d]/g, "")) || 0), 0);
  const sisa = Math.round(((Number(row.amount) || 0) - total) * 100) / 100;
  const setPart = (i, k, v) => setParts((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const addPart = () => setParts((ps) => (ps.length < 10 ? [...ps, { amount: "", note: "" }] : ps));
  const removePart = (i) => setParts((ps) => (ps.length > 2 ? ps.filter((_, idx) => idx !== i) : ps));
  return (
    <div className="border border-violet-300 bg-violet-50/40 p-3 space-y-2" data-testid="bd-split-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="overline text-[9px] text-violet-800 tracking-widest">Pecah {formatIDR(row.amount)} menjadi beberapa transaksi</div>
        <button type="button" onClick={addPart} disabled={parts.length >= 10} data-testid="bd-split-add"
          className="inline-flex items-center gap-1 px-2 py-0.5 border border-violet-400 text-violet-700 hover:bg-violet-100 text-[10px] font-semibold disabled:opacity-40">
          <Plus size={10} /> Tambah Pecahan
        </button>
      </div>
      <div className="space-y-1.5">
        {parts.map((p, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`bd-split-part-row-${i}`}>
            <span className="w-8 text-[10px] text-slate-500 tabular-nums">#{i + 1}</span>
            <input inputMode="numeric" value={p.amount === "" ? "" : Number(p.amount).toLocaleString("id-ID")}
              onChange={(e) => setPart(i, "amount", e.target.value.replace(/[^\d]/g, ""))}
              placeholder="Nominal pecahan" data-testid={`bd-split-amount-${i}`}
              className="w-36 border border-violet-300 px-2 py-1 text-right tabular-nums text-[11px] bg-white focus:outline-none" />
            <input value={p.note} onChange={(e) => setPart(i, "note", e.target.value)} maxLength={120}
              placeholder="Catatan (opsional)" data-testid={`bd-split-note-${i}`}
              className="flex-1 border border-violet-300 px-2 py-1 text-[11px] bg-white focus:outline-none" />
            <button type="button" onClick={() => removePart(i)} disabled={parts.length <= 2} title="Hapus pecahan"
              data-testid={`bd-split-remove-${i}`}
              className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <div className={`flex items-center justify-between gap-2 flex-wrap text-[11px] pt-1.5 border-t border-violet-200 ${sisa === 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="bd-split-summary">
        <span>Total pecahan: <b className="tabular-nums">{formatIDR(total)}</b></span>
        <span data-testid="bd-split-sisa">{sisa === 0 ? "✓ Sesuai nominal transaksi" : `Sisa belum terbagi: ${formatIDR(sisa)}`}</span>
      </div>
    </div>
  );
}

/** Panel kelola split yg sudah ada: trace + tombol Batalkan Split. */
function SplitManagePanel({ row, onSaved }) {
  const [busy, setBusy] = useState(false);
  const unsplit = async () => {
    if (!window.confirm("Batalkan split? Pecahan digabung kembali ke transaksi asli. Klasifikasi COA yang sudah diisi pada pecahan (tab Belum Diklasifikasi) akan DIHAPUS dan harus diisi ulang.")) return;
    try {
      setBusy(true);
      await unsplitEstatementRow(row.id);
      toast.success("Split dibatalkan — transaksi kembali utuh.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membatalkan split.");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="bd-split-manage">
      <SplitTrace row={row} />
      <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 p-2" data-testid="bd-split-hint">
        Baris ini sudah di-split — klasifikasi COA dilakukan per pecahan di tab <b>Belum Diklasifikasi</b>.
      </div>
      <button type="button" onClick={unsplit} disabled={busy} data-testid="bd-unsplit"
        className="inline-flex items-center gap-1 px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
        <X size={11} /> {busy ? "Membatalkan…" : "Batalkan Split"}
      </button>
    </div>
  );
}

function BreakdownDialog({ row, coa, projects, onClose, onSaved }) {
  const allowed = row.direction === "in" ? ["penerimaan", "transfer"] : ["pembayaran", "transfer"];
  const [kind, setKind] = useState(row.breakdown_kind || allowed[0]);
  const [coaId, setCoaId] = useState(row.coa_id || "");
  const [docNo, setDocNo] = useState(row.doc_no || "");
  const [note, setNote] = useState(row.note || "");
  const [invoiceNo, setInvoiceNo] = useState(row.invoice_no || "");
  const [poNo, setPoNo] = useState(row.po_no || "");
  const [picName, setPicName] = useState(row.pic_name || "");
  const [ketPembelian, setKetPembelian] = useState(row.keterangan_pembelian || "");
  const [projectId, setProjectId] = useState(row.project_id || "");
  const [saving, setSaving] = useState(false);
  // 2026-02 (user request): auto-saran COA berdasarkan deskripsi mutasi.
  const [suggested, setSuggested] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!row?.description) return;
    suggestCOA([row.description]).then((s) => {
      if (cancelled) return;
      const list = s[row.description] || [];
      setSuggested(list);
      // Auto-preselect top suggestion HANYA saat COA masih kosong (belum di-breakdown).
      if (!row.coa_id && list.length > 0) setCoaId(list[0].coa_id);
    });
    return () => { cancelled = true; };
  }, [row?.description, row?.coa_id]);

  // 2026-02-13 (user request): mode Split Nominal — pecah 1 transaksi jadi 2..10
  // pecahan; pecahan diklasifikasi terpisah di tab Belum Diklasifikasi.
  const existingSplit = row.split_parts || [];
  const [mode, setMode] = useState(existingSplit.length > 0 ? "split" : "klasifikasi");
  const [parts, setParts] = useState([{ amount: "", note: "" }, { amount: "", note: "" }]);

  const submitSplit = async () => {
    const parsed = parts.map((p) => ({ amount: parseFloat(String(p.amount).replace(/[^\d]/g, "")) || 0, note: (p.note || "").trim() || null }));
    if (parsed.length < 2) { toast.error("Split membutuhkan minimal 2 pecahan."); return; }
    if (parsed.some((p) => p.amount <= 0)) { toast.error("Semua nominal pecahan harus lebih dari 0."); return; }
    const total = parsed.reduce((a, p) => a + p.amount, 0);
    if (Math.abs(total - (Number(row.amount) || 0)) > 0.01) {
      toast.error(`Total pecahan (${formatIDR(total)}) harus sama persis dengan nominal transaksi (${formatIDR(row.amount)}).`);
      return;
    }
    try {
      setSaving(true);
      await splitEstatementRow(row.id, parsed);
      toast.success(`Transaksi di-split menjadi ${parsed.length} pecahan — pecahan muncul di tab Belum Diklasifikasi untuk diisi COA.`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan split.");
    } finally { setSaving(false); }
  };

  const payload = () => ({
    kind, coa_id: coaId || null, doc_no: docNo || null, note: note || null,
    invoice_no: invoiceNo || null, po_no: poNo || null, pic_name: picName || null,
    keterangan_pembelian: ketPembelian || null, project_id: projectId || null,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "split") return; // mode split punya tombol "Simpan Split" sendiri (type=button)
    try {
      setSaving(true);
      await breakdownRow(row.id, payload());
      toast.success("Breakdown disimpan.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal.");
    } finally { setSaving(false); }
  };

  const clear = async () => {
    try {
      setSaving(true);
      await breakdownRow(row.id, { kind: null, coa_id: null, doc_no: null, note: null, invoice_no: null, po_no: null, pic_name: null, keterangan_pembelian: null, project_id: null });
      toast.success("Breakdown dihapus.");
      onSaved?.();
    } catch (err) { toast.error("Gagal."); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="bd-dialog">
      <form onSubmit={submit} className="bg-white w-full max-w-xl border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="overline text-[10px] text-slate-500 tracking-widest">BREAKDOWN BARIS</div>
            <div className="font-display text-base font-semibold text-[#0B1D3A]">{fmtDate(row.date)} · {formatIDR(row.amount)}</div>
            <div className="text-[10px] text-slate-500">{row.description}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-[11px] max-h-[70vh] overflow-y-auto">
          {/* 2026-02-13 (user request): 2 mode — Klasifikasi Langsung vs Split Nominal. */}
          <div className="flex items-center gap-1" data-testid="bd-mode-toggle">
            <button type="button" onClick={() => setMode("klasifikasi")} disabled={existingSplit.length > 0}
              title={existingSplit.length > 0 ? "Baris sudah di-split — klasifikasi dilakukan per pecahan" : undefined}
              data-testid="bd-mode-klasifikasi"
              className={`px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border transition-colors disabled:opacity-40 ${mode === "klasifikasi" ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-500 border-slate-300 hover:text-[#0B1D3A] hover:border-[#0B1D3A]"}`}>
              Klasifikasi Langsung
            </button>
            <button type="button" onClick={() => setMode("split")} data-testid="bd-mode-split"
              className={`inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wider font-semibold border transition-colors ${mode === "split" ? "bg-violet-700 text-white border-violet-700" : "bg-white text-slate-500 border-slate-300 hover:text-violet-700 hover:border-violet-400"}`}>
              <Split size={11} /> Split Nominal
            </button>
          </div>
          {mode === "split" ? (
            existingSplit.length > 0 ? (
              <SplitManagePanel row={row} onSaved={onSaved} />
            ) : (
              <SplitEditor row={row} parts={parts} setParts={setParts} />
            )
          ) : (<>
          <div>
            <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Jenis</div>
            <div className="flex items-center gap-1 flex-wrap">
              {KIND_ORDER.map((k) => {
                const disabledK = !allowed.includes(k);
                return (
                  <button key={k} type="button" disabled={disabledK} onClick={() => setKind(k)} data-testid={`bd-kind-${k}`}
                    className={`px-3 py-1 border text-[10px] uppercase tracking-wider font-semibold ${kind === k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : disabledK ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]"}`}>
                    {KIND_META[k].label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">COA (AKUN COA)</div>
            <SearchableSelect value={coaId} onChange={setCoaId}
              groups={buildCoaGroupsWithSuggestions(suggested, coa)}
              placeholder="— Tanpa Kategori —" testid="bd-coa" />
            {suggested.length > 0 && (
              <div className="text-[10px] text-emerald-700 mt-1" data-testid="bd-suggest-hint">
                🔮 Disarankan: <b>{suggested[0].code} · {suggested[0].name}</b> (cocok kata kunci "{suggested[0].keyword}")
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Invoice / PI No</div>
              <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} maxLength={64} data-testid="bd-field-invoice"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">No Purchase Order</div>
              <input value={poNo} onChange={(e) => setPoNo(e.target.value)} maxLength={64} data-testid="bd-field-po"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Kode Voucher</div>
              <input value={docNo} onChange={(e) => setDocNo(e.target.value)} maxLength={64} data-testid="bd-field-docno"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">PIC / Vendor Name</div>
              <input value={picName} onChange={(e) => setPicName(e.target.value)} maxLength={100} data-testid="bd-field-pic"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Proyek</div>
              <SearchableSelect value={projectId} onChange={setProjectId}
                options={buildProjectOptions(projects)}
                placeholder="— Tanpa Proyek —" testid="bd-field-project" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Keterangan Pembelian</div>
              <input value={ketPembelian} onChange={(e) => setKetPembelian(e.target.value)} maxLength={200} data-testid="bd-field-ket"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white" />
            </div>
          </div>
          <div>
            <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Catatan Internal</div>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} data-testid="bd-field-note"
              className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white" />
          </div>
          </>)}
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/70">
          <div>
            {mode === "klasifikasi" && row.breakdown_kind && (
              <button type="button" onClick={clear} disabled={saving} data-testid="bd-clear"
                className="px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] uppercase tracking-wider font-semibold">Hapus Breakdown</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">{mode === "split" && existingSplit.length > 0 ? "Tutup" : "Batal"}</button>
            {mode === "klasifikasi" ? (
              <button type="submit" disabled={saving} data-testid="bd-save"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
                <Save size={12} /> {saving ? "Menyimpan…" : "Simpan"}
              </button>
            ) : existingSplit.length === 0 ? (
              <button type="button" onClick={submitSplit} disabled={saving} data-testid="bd-split-save"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-violet-700 hover:bg-violet-800 text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
                <Split size={12} /> {saving ? "Menyimpan…" : "Simpan Split"}
              </button>
            ) : null}
          </div>
        </div>
      </form>
    </div>
  );
}

function BulkBreakdownDialog({ rows, coa, onClose, onSaved }) {
  // Jenis yang diizinkan = irisan arah semua baris terpilih:
  // masuk → penerimaan/transfer · keluar → pembayaran/transfer · campur → transfer saja.
  const hasIn = rows.some((r) => r.direction === "in");
  const hasOut = rows.some((r) => r.direction === "out");
  const allowed = KIND_ORDER.filter((k) =>
    k === "transfer" || (k === "penerimaan" && !hasOut) || (k === "pembayaran" && !hasIn));
  const [kind, setKind] = useState(allowed[0] || "transfer");
  const [coaId, setCoaId] = useState("");
  const [docNo, setDocNo] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  // 2026-02 (user request): saran COA agregat utk baris terpilih.
  const [suggested, setSuggested] = useState([]);
  const [autoSelectedRatio, setAutoSelectedRatio] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const descs = Array.from(new Set(rows.map((r) => (r?.description || "").trim()).filter(Boolean)));
    if (descs.length === 0) return;
    suggestCOA(descs).then((sMap) => {
      if (cancelled) return;
      // Hitung berapa baris top-1-nya masing2 COA.
      const votes = new Map(); // coa_id -> {count, meta}
      for (const r of rows) {
        const top = (sMap[r.description] || [])[0];
        if (!top) continue;
        const cur = votes.get(top.coa_id) || { count: 0, meta: top };
        cur.count += 1;
        votes.set(top.coa_id, cur);
      }
      const ranked = Array.from(votes.values()).sort((a, b) => b.count - a.count);
      const agg = ranked.map((v) => ({ ...v.meta, count: v.count }));
      setSuggested(agg);
      if (agg.length > 0) {
        const ratio = agg[0].count / rows.length;
        setAutoSelectedRatio(ratio);
        // Auto pilih hanya bila mayoritas (≥50%) baris cocok ke COA yang sama.
        if (ratio >= 0.5 && !coaId) setCoaId(agg[0].coa_id);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await bulkBreakdownRows({
        row_ids: rows.map((r) => r.id), kind,
        coa_id: coaId || null, doc_no: docNo || null, note: note || null,
      });
      toast.success(`Breakdown massal disimpan: ${res.updated ?? rows.length} baris.`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan breakdown massal.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="bd-bulk-dialog">
      <form onSubmit={submit} className="bg-white w-full max-w-lg border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="overline text-[10px] text-slate-500 tracking-widest">BREAKDOWN MASSAL</div>
            <div className="font-display text-base font-semibold text-[#0B1D3A]" data-testid="bd-bulk-count">{rows.length} baris · {formatIDR(total)}</div>
            {hasIn && hasOut && (
              <div className="text-[10px] text-amber-700 mt-0.5">Seleksi campur masuk & keluar — hanya jenis Transfer tersedia.</div>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-[11px]">
          <div>
            <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Jenis (berlaku utk semua baris)</div>
            <div className="flex items-center gap-1 flex-wrap">
              {KIND_ORDER.map((k) => {
                const disabledK = !allowed.includes(k);
                return (
                  <button key={k} type="button" disabled={disabledK} onClick={() => setKind(k)} data-testid={`bd-bulk-kind-${k}`}
                    className={`px-3 py-1 border text-[10px] uppercase tracking-wider font-semibold ${kind === k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : disabledK ? "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]"}`}>
                    {KIND_META[k].label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">COA</div>
            <SearchableSelect value={coaId} onChange={setCoaId}
              groups={buildCoaGroupsWithSuggestions(suggested, coa)}
              placeholder="— Tanpa Kategori —" testid="bd-bulk-coa" />
            {suggested.length > 0 && (
              <div className="text-[10px] text-emerald-700 mt-1" data-testid="bd-bulk-suggest-hint">
                🔮 Disarankan: <b>{suggested[0].code} · {suggested[0].name}</b> · cocok pada {suggested[0].count} dari {rows.length} baris
                {autoSelectedRatio >= 0.5 ? " (auto-pilih)" : ""}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">No Dokumen</div>
              <input value={docNo} onChange={(e) => setDocNo(e.target.value)} maxLength={64} data-testid="bd-bulk-doc"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white" />
            </div>
            <div>
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1">Catatan</div>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} data-testid="bd-bulk-note"
                className="w-full border border-slate-300 px-2 py-1.5 text-[11px] bg-white" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50/70">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold">Batal</button>
          <button type="submit" disabled={saving} data-testid="bd-bulk-save"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF5A1F] hover:bg-[#e34d16] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
            <Save size={12} /> {saving ? "Menyimpan…" : `Simpan ke ${rows.length} Baris`}
          </button>
        </div>
      </form>
    </div>
  );
}
